<template>
  <section>
    <h2 class="mb-0 d-none d-lg-block">{{ $t('designFontSelect.title') }}</h2>
    <div ref="font-selector-panel" class="pl-3 font-selector-panel">
      <b-row v-for="(splitOption, i) in splitOptions" :key="`font-selector-row-${i}`" class="my-3 flex-nowrap">
        <b-col
          v-for="option in splitOption"
          :key="`${option.fontFamily}`"
          :ref="option.fontFamily === font.fontFamily ? `selected-font` : `${option.fontFamily}`"
          class="font-selector-col p-1"
        >
          <b-button
            :title="option.fontFamily"
            class="font-option text-center d-inline-flex align-items-center justify-content-center p-1"
            :class="{ selected: option.fontFamily === font.fontFamily }"
            :style="{ fontFamily: option.fontFamily, cursor: 'pointer', fontSize: option.displaySize }"
            variant="outline-primary"
            @click="setFont(option)"
          >
            {{ option.fontFamily }}
          </b-button>
        </b-col>
      </b-row>
    </div>
    <input id="product-style-font" :value="font.fontFamily" type="text" class="d-none" hidden />
  </section>
</template>

<script>
export default {
  name: 'DesignFontSelect',
  props: {
    value: {
      type: [String, Object],
      required: false,
      default: '',
    },
  },
  data() {
    return {
      font: this.value,
    };
  },
  computed: {
    options() {
      return [
        {
          fontFamily: 'Bebas Neue',
          displaySize: '-webkit-xxx-large',
        },
        {
          fontFamily: 'Amatic SC',
          displaySize: '-webkit-xxx-large',
        },
        {
          fontFamily: 'Bangers',
          displaySize: '-webkit-xxx-large',
        },
        {
          fontFamily: 'Carter One',
          displaySize: 'xx-large',
        },
        {
          fontFamily: 'Cinzel Decorative',
          displaySize: 'x-large',
        },
        {
          fontFamily: 'Dancing Script',
          displaySize: 'xx-large',
          text2: {
            relativeOffsetY: 5,
          },
        },
        {
          fontFamily: 'Emilys Candy',
          displaySize: 'xx-large',
          text2: {
            relativeOffsetY: 10,
          },
        },
        {
          fontFamily: 'Fredoka One',
          displaySize: 'x-large',
          text2: {
            relativeOffsetY: 5,
          },
        },
        {
          fontFamily: 'Graduate',
          displaySize: 'xx-large',
          text2: {
            relativeOffsetY: 5,
          },
        },
        {
          fontFamily: 'Grenze Gotisch',
          displaySize: 'xx-large',
        },
        {
          fontFamily: 'Life Savers',
          displaySize: 'xx-large',
          text2: {
            relativeOffsetY: 10,
          },
        },
        {
          fontFamily: 'Love Ya Like A Sister',
          displaySize: 'x-large',
        },
        {
          fontFamily: 'Notable',
          displaySize: 'xx-large',
          text2: {
            relativeOffsetY: 10,
          },
        },
        {
          fontFamily: 'Pacifico',
          displaySize: '-webkit-xxx-large',
          text2: {
            relativeOffsetY: 10,
          },
        },
        {
          fontFamily: 'Pompiere',
          displaySize: '-webkit-xxx-large',
          text2: {
            relativeOffsetY: 5,
          },
        },
        {
          fontFamily: 'Rye',
          displaySize: '-webkit-xxx-large',
          text2: {
            relativeOffsetY: 5,
          },
        },
        {
          fontFamily: 'Shrikhand',
          displaySize: 'xx-large',
          text2: {
            relativeOffsetY: 5,
          },
        },
        {
          fontFamily: 'Special Elite',
          displaySize: 'x-large',
        },
        {
          fontFamily: 'Unica One',
          displaySize: 'xx-large',
        },
        {
          fontFamily: 'UnifrakturMaguntia',
          displaySize: 'larger',
          text2: {
            relativeOffsetY: 5,
          },
        },
        {
          fontFamily: 'Vampiro One',
          displaySize: 'x-large',
          text2: {
            relativeOffsetY: 5,
          },
        },
        {
          fontFamily: 'ZCOOL KuaiLe',
          displaySize: 'x-large',
        },
      ];
    },
    splitOptions() {
      const rawOptions = [...this.options];
      const chunkOfOptions = [],
        half = Math.ceil(rawOptions.length / 2);
      chunkOfOptions.push(rawOptions.slice(0, half));
      chunkOfOptions.push(rawOptions.slice(-half + (rawOptions.length % 2)));
      return chunkOfOptions;
    },
  },
  created() {
    if (this.font?.fontFamily === 'Amatic SC') {
      this.setFont(this.options?.[0]);
    }
  },
  mounted() {
    if (this.value || this.font) this.scrollToSelected();
  },
  updated() {
    this.$nextTick(() => {
      if (!this.font?.fontFamily) {
        this.setFont(this.options?.[0]);
      }
    });
  },
  methods: {
    setFont(font) {
      if (font == this.font) {
        return;
      } else {
        this.font = font;
      }
      this.$emit('input', this.font);
      this.$emit('change');
    },
    scrollToSelected(font) {
      this.$nextTick(() => {
        this.$refs['font-selector-panel']?.scroll({
          top: 0,
          left: this.$refs[font?.fontFamily || 'selected-font']?.[0]?.offsetLeft - 40 || 0,
          behavior: 'smooth',
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.font-option {
  border-radius: 10px;
  border: 1px solid $dp-gray-4;
  width: 200px;
  height: 80px;
  color: $dp-text;
  &.selected {
    border: 2px solid $dp-blue;
    background-color: rgba(204, 236, 249, 0.5);
  }
  &:hover,
  &:active,
  &:focus {
    &:not(.disabled) {
      color: $dp-text !important;
      border: 2px solid $dp-blue;
      box-shadow: 0 0 0 0.2rem $dp-light-blue;
      background-color: rgba(204, 236, 249, 0.5) !important;
    }
  }
}

.font-selector-panel {
  overflow-x: scroll;
  overflow-y: hidden;
  &::-webkit-scrollbar {
    background-color: $dp-gray-2;
    height: 0.75rem !important;
  }
  &::-webkit-scrollbar-thumb {
    background-color: $secondary;
    border-radius: 10px;
  }
}

.font-selector-col {
  margin-right: 1em;
  &:last-child {
    margin-right: 0;
  }
}
</style>
